import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { DateFormatter } from '@repo/toolkit/date'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { startOfDay } from 'date-fns'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../../lib/hooks/use-translate'
import { CenterCourseInput } from '../../../../lib/types/models/center-course/center-course-input'
import { BaseFormProps } from '../../../../lib/types/utils'
import { getSubmitAction } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { createTimeSchema } from '../../../../lib/utils/validations/date.validation'
import { createStringSchema } from '../../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form, FormControl, FormField, FormItem } from '../../../elements/forms/form'
import ControlledDateStringInput from '../../../elements/inputs/controlled-date-string-input'
import ControlledHourInput from '../../../elements/inputs/controlled-hour-input'
import ValidationButtons from '../../../elements/validation-buttons'

const getFormSchema = (t: TFunction<AvailableLanguages, undefined>): ZodType<CenterCourseInput> =>
  z
    .object({
      date: createStringSchema(t('ol.center.program.course.form.date')),
      startingTime: createTimeSchema({ inputName: t('ol.center.program.course.form.start.time') }),
      endingTime: createTimeSchema({ inputName: t('ol.center.program.course.form.end.time') })
    })
    .superRefine((data, ctx) => {
      // the starting time must be before the ending time
      const startingTime = data.startingTime
      const endingTime = data.endingTime

      if (DateFormatter.isTimeGreaterThanOrEqualTo(startingTime, endingTime)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Starting time must be before ending time.',
          path: ['startingTime'] // This will add the error to the startingTime field
        })
      }
    })
type Props = BaseFormProps<CenterCourseInput, true> & {
  className?: string
  startDate?: Date
  endDate?: Date
}

const CenterCourseForm = ({
  defaultValues,
  className,
  startDate,
  endDate,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()

  const form = useForm<CenterCourseInput>({
    resolver: zodResolver(getFormSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className={cn('flex flex-col gap-3', className)}>
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormControl>
                  <ControlledDateStringInput
                    {...field}
                    startDate={startDate ? startOfDay(startDate) : undefined}
                    endDate={endDate ? startOfDay(endDate) : undefined}
                    required
                    mode="over"
                    label={t('ol.center.program.course.form.date')}
                  />
                </FormControl>
              </FormItem>
            )}
          />
          <div className="flex items-center">
            <FormField
              control={form.control}
              name={'startingTime'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput
                      {...field}
                      required
                      label={t('ol.center.program.course.form.start.time')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
            <div className="w-3" />
            <FormField
              control={form.control}
              name={'endingTime'}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <ControlledHourInput
                      {...field}
                      required
                      label={t('ol.center.program.course.form.end.time')}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </div>
        <ValidationButtons {...validationButtonProps} hasBorderTop />
      </form>
    </Form>
  )
}

export default CenterCourseForm
